import { computed, ref } from "vue";

const DEFAULT_FLOOR_HEIGHT = 200;
const DEFAULT_PADDING = 18;
const DEFAULT_MAIN_AXIS_OFFSET = 7.5;

interface UseTriggerAvailableHeightOptions {
  floorHeight?: number;
  padding?: number;
}

/**
 * Sizes a floating dropdown's content to the real remaining viewport space, without ever
 * reading the popover's own (self-referential) rect or size. Measures the trigger element
 * instead — call `calculate` on the dropdown's `opened` event, which fires before floating-ui
 * positions the popover for that open cycle, so the content is already at its final size before
 * any placement decision is made and there's nothing left to grow into afterward.
 */
export function useTriggerAvailableHeight(options: UseTriggerAvailableHeightOptions = {}) {
  const floorHeight = options.floorHeight ?? DEFAULT_FLOOR_HEIGHT;
  const padding = options.padding ?? DEFAULT_PADDING;

  const triggerElRef = ref<HTMLElement | null>(null);
  const maxHeight = ref<number | null>(null);
  const style = computed(() => (maxHeight.value != null ? { maxHeight: `${maxHeight.value}px` } : {}));

  function calculate(placement = "bottom-start", offsetY = DEFAULT_MAIN_AXIS_OFFSET) {
    if (!triggerElRef.value) return;

    const rect = triggerElRef.value.getBoundingClientRect();
    const side = placement.split("-")[0];

    let availableHeight: number;
    if (side === "top") {
      availableHeight = rect.top - offsetY - padding;
    } else if (side === "bottom") {
      availableHeight = window.innerHeight - rect.bottom - offsetY - padding;
    } else {
      // left/right placements: offsetY maps to the horizontal main axis there, not vertical
      // position, so the popover's vertical position tracks the trigger's own top edge.
      availableHeight = window.innerHeight - rect.top - padding;
    }

    maxHeight.value = Math.max(floorHeight, availableHeight);
  }

  return { triggerElRef, style, calculate };
}
